import React, { useEffect, useState } from 'react';
import { streamsService, LearningStream } from '../services/streams';
import { logsService, DailyLog } from '../services/logs';
import { papersService, ResearchPaper } from '../services/papers';
import { projectsService, Project } from '../services/projects';
import StreamCard from './StreamCard';
import ProgressChart from './ProgressChart';
import UpcomingMilestones from './UpcomingMilestones';

const Dashboard: React.FC = () => {
  const [streams, setStreams] = useState<LearningStream[]>([]);
  const [recentLogs, setRecentLogs] = useState<DailyLog[]>([]);
  const [papers, setPapers] = useState<ResearchPaper[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [streamsData, logsData, papersData, projectsData] = await Promise.all([
        streamsService.getAll(),
        logsService.getAll(undefined, undefined, undefined),
        papersService.getAll(),
        projectsService.getAll(),
      ]);

      setStreams(streamsData);
      setRecentLogs(logsData.slice(0, 7)); // Last 7 days
      setPapers(papersData);
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading dashboard...</div>
      </div>
    );
  }

  // Calculate overall progress
  const totalTargetHours = streams.reduce((sum, s) => sum + s.target_hours, 0);
  const totalCompletedHours = streams.reduce((sum, s) => sum + s.completed_hours, 0);
  const overallProgress = totalTargetHours > 0 
    ? Math.round((totalCompletedHours / totalTargetHours) * 100) 
    : 0;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header - Hidden on mobile as it's in the top bar */}
        <div className="mb-6 md:mb-8 hidden lg:block">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2">
            Learning Dashboard
          </h1>
          <p className="text-sm md:text-base text-gray-600">
            Track your progress towards AI + Quantum Computing Masters preparation
          </p>
        </div>

        {/* Overall Progress */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-2xl font-semibold mb-4">Overall Progress</h2>
          <div className="w-full bg-gray-200 rounded-full h-6 mb-2">
            <div
              className="bg-primary h-6 rounded-full transition-all duration-300"
              style={{ width: `${overallProgress}%` }}
            >
              <span className="text-white text-sm font-medium px-2 flex items-center h-full">
                {overallProgress}%
              </span>
            </div>
          </div>
          <div className="flex justify-between text-sm text-gray-600">
            <span>{totalCompletedHours.toFixed(1)} hours completed</span>
            <span>{totalTargetHours} hours target</span>
          </div>
        </div>

        {/* Stream Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {streams.map((stream) => (
            <StreamCard key={stream.id} stream={stream} />
          ))}
        </div>

        {/* Charts and Additional Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ProgressChart logs={recentLogs} streams={streams} />
          <UpcomingMilestones streams={streams} />
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-2">Research Papers</h3>
            <p className="text-3xl font-bold text-accent">
              {papers.filter(p => p.status === 'Complete').length}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              of {papers.length} total papers
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-2">Portfolio Projects</h3>
            <p className="text-3xl font-bold text-secondary">
              {projects.filter(p => p.status === 'Complete' || p.status === 'Published').length}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              of {projects.length} total projects
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-2">Daily Logs</h3>
            <p className="text-3xl font-bold text-primary">
              {recentLogs.length}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              logs in last 7 days
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

